import { useEffect, useRef } from "react";

/* Lazy Three.js orb field for the "Step into the cove" section: sound sources
   as warm glowing orbs drifting in the dark around the cove image, with gentle
   pointer parallax. Three is dynamically imported only once the section nears
   the viewport; skipped entirely under prefers-reduced-motion or without WebGL. */

/* Diegetic staging (art-director pass): the copy says "the talk track just off
   your shoulder, rain far behind, music low in front" — the orbs ARE those
   layers. All x >= 0 so they live over the artwork/seam, never the text.
   Opacity encodes depth: near ~0.8 → far ~0.3. */
const ORBS = [
  { color: 0xffb454, x: 0.6, y: 0.5, z: -0.6, r: 0.34 }, // talk — off your shoulder
  { color: 0xb39ddb, x: 1.8, y: -0.9, z: -1.4, r: 0.24 }, // music — low in front
  { color: 0x47c9ad, x: 2.9, y: 1.1, z: -2.8, r: 0.16 }, // rain — far, over the water
  { color: 0xffd27d, x: 2.2, y: -0.2, z: -3.4, r: 0.1 }, // deep bokeh speck
];

export function CoveScene() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let disposed = false;
    let cleanup: (() => void) | undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        void init();
      },
      { rootMargin: "400px" },
    );
    observer.observe(host);

    async function init() {
      const THREE = await import("three");
      if (disposed || !host) return;

      let renderer: import("three").WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      } catch {
        return;
      }
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.setSize(host.clientWidth, host.clientHeight);
      renderer.domElement.style.position = "absolute";
      renderer.domElement.style.inset = "0";
      host.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        50,
        host.clientWidth / host.clientHeight,
        0.1,
        50,
      );
      camera.position.z = 5;

      // soft radial glow sprite, drawn once on a canvas
      const glowCanvas = document.createElement("canvas");
      glowCanvas.width = glowCanvas.height = 128;
      const g = glowCanvas.getContext("2d")!;
      const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
      grad.addColorStop(0, "rgba(255,255,255,1)");
      grad.addColorStop(0.35, "rgba(255,255,255,0.45)");
      grad.addColorStop(1, "rgba(255,255,255,0)");
      g.fillStyle = grad;
      g.fillRect(0, 0, 128, 128);
      const glowTex = new THREE.CanvasTexture(glowCanvas);

      const sprites = ORBS.map((orb) => {
        const mat = new THREE.SpriteMaterial({
          map: glowTex,
          color: orb.color,
          transparent: true,
          opacity: Math.max(0.3, 0.9 + orb.z * 0.18),
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const sprite = new THREE.Sprite(mat);
        sprite.position.set(orb.x, orb.y, orb.z);
        sprite.scale.setScalar(orb.r * 3);
        scene.add(sprite);
        return sprite;
      });

      let px = 0;
      let py = 0;
      const onPointer = (e: PointerEvent) => {
        const rect = host.getBoundingClientRect();
        px = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        py = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      };
      host.addEventListener("pointermove", onPointer);

      const onResize = () => {
        camera.aspect = host.clientWidth / host.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(host.clientWidth, host.clientHeight);
      };
      addEventListener("resize", onResize);

      const clock = new THREE.Clock();
      let raf = 0;
      const tick = () => {
        const t = clock.getElapsedTime();
        sprites.forEach((sprite, i) => {
          const orb = ORBS[i];
          sprite.position.x = orb.x + Math.sin(t * 0.25 + i * 1.7) * 0.35;
          sprite.position.y = orb.y + Math.cos(t * 0.2 + i * 2.3) * 0.3;
          // presences, not fireflies: slow, shallow pulse
          const pulse = 1 + Math.sin(t * 0.45 + i) * 0.04;
          sprite.scale.setScalar(orb.r * 3 * pulse);
        });
        // scroll parallax: the orb field rides the section through the viewport
        const rect = host!.getBoundingClientRect();
        const progress = Math.min(
          1,
          Math.max(0, (innerHeight - rect.top) / (innerHeight + rect.height)),
        );
        scene.position.y = 0.4 - 0.8 * progress;
        camera.position.x += (px * 0.45 - camera.position.x) * 0.04;
        camera.position.y += (-py * 0.3 - camera.position.y) * 0.04;
        camera.lookAt(0, scene.position.y, -1.5);
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      };
      tick();

      cleanup = () => {
        cancelAnimationFrame(raf);
        host.removeEventListener("pointermove", onPointer);
        removeEventListener("resize", onResize);
        glowTex.dispose();
        sprites.forEach((sprite) => sprite.material.dispose());
        renderer.dispose();
        renderer.domElement.remove();
      };
    }

    return () => {
      disposed = true;
      observer.disconnect();
      cleanup?.();
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      className="pointer-events-auto absolute inset-0 overflow-hidden"
    />
  );
}
